import { PayloadAction } from '@reduxjs/toolkit';
import {
  userLogin,
  userSocialLogin,
  IUserLoginResponse,
  fetchUserById,
  userLogout,
  impersonateUser,
  twoFactorAuthLogin,
  fetchWhoAmI,
  userSocialSignUpPro,
  IUserLoginErrorResponse
} from './login.thunks';
import { AuthState, LoggingMode } from './authState';
import { IUser } from '../users/user';
import { userSignUp, userSocialSignUp } from '../signup/signup.thunks';

const userLoginPendingReducer = (state: AuthState): void => {
  state.loading = true;
  state.error = null;
  state.isLoggedIn = false;
  state.loggingMode = LoggingMode.None;
  state.showReCaptchaV2ForLogin = false;
};

const userLoginFulfilledReducer = (state: AuthState, action: PayloadAction<IUserLoginResponse>): void => {
  state.token = action.payload.token;
  state.userId = action.payload.userId;
  state.payloadType = action.payload.payloadType;
  state.hash = action.payload.hash;
  state.isLoggedIn = true;
  state.loading = false;
  state.loggingMode = LoggingMode.Credentials;
  state.error = null;
  state.showReCaptchaV2ForLogin = false;
};

const userSignUpRejectedReducer = (state: AuthState, action: PayloadAction<string>): void => {
  state.loading = false;
  state.error = action.payload;
  state.loggingMode = LoggingMode.Credentials;
};

const userLoginRejectedReducer = (state: AuthState, action: PayloadAction<IUserLoginErrorResponse>): void => {
  const { message, recaptchaError } = action.payload;
  state.loading = false;
  state.error = message;
  state.loggingMode = LoggingMode.Credentials;
  state.showReCaptchaV2ForLogin = !!recaptchaError;
};

const userSocialLoginPendingReducer = (state: AuthState): void => {
  state.loading = true;
  state.error = null;
  state.isLoggedIn = false;
  state.loggingMode = LoggingMode.None;
};

const userSocialLoginFulfilledReducer = (state: AuthState, action: PayloadAction<IUserLoginResponse>): void => {
  state.token = action.payload?.token;
  state.userId = action.payload?.userId;
  state.isLoggedIn = true;
  state.loading = false;
  state.loggingMode = LoggingMode.Social;
};

const userSocialLoginRejectedReducer = (state: AuthState, action: PayloadAction<string>): void => {
  state.loading = false;
  state.error = action.payload;
  state.loggingMode = LoggingMode.Social;
};

const reducers = {
  [userLogin?.pending.type]: userLoginPendingReducer,
  [userLogin?.fulfilled.type]: userLoginFulfilledReducer,
  [userLogin?.rejected.type]: userLoginRejectedReducer,
  [userSignUp?.pending.type]: userLoginPendingReducer,
  [userSignUp?.fulfilled.type]: userLoginFulfilledReducer,
  [userSignUp?.rejected.type]: userSignUpRejectedReducer,
  [userSocialLogin?.pending.type]: userSocialLoginPendingReducer,
  [userSocialLogin?.fulfilled.type]: userSocialLoginFulfilledReducer,
  [userSocialLogin?.rejected.type]: userSocialLoginRejectedReducer,
  [userSocialSignUp?.pending.type]: userSocialLoginPendingReducer,
  [userSocialSignUp?.fulfilled.type]: userSocialLoginFulfilledReducer,
  [userSocialSignUp?.rejected.type]: userSocialLoginRejectedReducer,
  [userSocialSignUpPro?.pending.type]: userSocialLoginPendingReducer,
  [userSocialSignUpPro?.fulfilled.type]: userSocialLoginFulfilledReducer,
  [userSocialSignUpPro?.rejected.type]: userSocialLoginRejectedReducer,
  [fetchUserById?.pending.type]: (state: AuthState): void => {
    state.loading = true;
    state.error = null;
  },
  [fetchUserById?.fulfilled.type]: (state: AuthState, action: PayloadAction<IUser>): void => {
    state.user = action.payload;
    state.loading = false;
  },
  [fetchUserById?.rejected.type]: (state: AuthState, action: PayloadAction<string>): void => {
    state.loading = false;
    state.error = action.payload;
  },
  [fetchWhoAmI?.pending.type]: (state: AuthState): void => {
    state.loading = true;
    state.error = null;
  },
  [fetchWhoAmI?.fulfilled.type]: (state: AuthState, action: PayloadAction<IUser>): void => {
    state.user = action.payload;
    state.loading = false;
  },
  [fetchWhoAmI?.rejected.type]: (state: AuthState, action: PayloadAction<string>): void => {
    state.loading = false;
    state.error = action.payload;
  },
  [userLogout?.pending.type]: (state: AuthState): void => {
    state.loading = true;
    state.error = null;
  },
  [userLogout?.fulfilled.type]: (state: AuthState): void => {
    state.loading = false;
    state.token = '';
    state.isLoggedIn = false;
    state.userId = null;
    state.user = null;
    state.uniqueSession = true;
    state.payloadType = null;
    state.hash = null;
    state.loggingMode = LoggingMode.None;
  },
  [userLogout?.rejected.type]: (state: AuthState, action: PayloadAction<string>): void => {
    state.loading = false;
    state.error = action.payload;
  },
  [impersonateUser?.pending.type]: (state: AuthState): void => {
    state.loadingImpersonate = true;
  },
  [impersonateUser?.fulfilled.type]: (state: AuthState): void => {
    state.loadingImpersonate = false;
  },
  [impersonateUser?.rejected.type]: (state: AuthState): void => {
    state.loadingImpersonate = false;
  },
  [twoFactorAuthLogin?.pending.type]: (state: AuthState): void => {
    state.loading = true;
    state.error = null;
    state.isLoggedIn = false;
  },
  [twoFactorAuthLogin?.fulfilled.type]: (state: AuthState, action: PayloadAction<IUserLoginResponse>): void => {
    state.token = action.payload.token;
    state.userId = action.payload.userId;
    state.payloadType = action.payload.payloadType;
    state.hash = null;
    state.isLoggedIn = true;
    state.loading = false;
  },
  [twoFactorAuthLogin?.rejected.type]: (state: AuthState, action: PayloadAction<string>): void => {
    state.loading = false;
    state.error = action.payload;
  }
};

export default reducers;
